/**
 * ProjectAutomationsPanel — embedded inside a project's detail view.
 *
 * Shows three grouped sub-sections for the given project, in this order:
 *   1. Project-Specific  — automations whose project_scope='specific' AND this project is in project_ids
 *   2. Shared            — automations whose project_scope='all' (excluding email-received)
 *   3. Email Automations — automations with template_type='email_received_alert'
 *
 * Clicking any card opens the same AutomationDetailModal used on the AI Automations page.
 * Toggle / delete / edit all hit the same backend endpoints.
 *
 * NOTE: data-testid="project-automations-panel" is always mounted immediately so
 * tests can find it without waiting for the loading state to resolve.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Envelope, Globe, Target, ArrowsClockwise, Lightning, ArrowRight,
} from "@phosphor-icons/react";
import {
  getAutomations, toggleAutomation, deleteAutomation, updateAutomation,
} from "../../api/automations";
import AutomationsSection from "./AutomationsSection";
import AutomationDetailModal from "./AutomationDetailModal";

export default function ProjectAutomationsPanel({ project, projects = [] }) {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [toggling, setToggling]   = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [selected, setSelected]   = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAutomations();
        if (!cancelled) setAutomations(data);
      } catch {
        if (!cancelled) toast.error("Failed to load automations for this project.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Grouped for this specific project
  const { projectSpecific, shared, email } = useMemo(() => {
    const projectSpecific = [];
    const shared = [];
    const email = [];
    for (const a of automations) {
      // All email-triggered automations (received-alerts + project router) go under Email
      if (a.template_type === "email_received_alert" || a.template_type === "email_project_router") {
        email.push(a);
      } else if (a.project_scope === "specific" && a.project_ids?.includes(project.id)) {
        projectSpecific.push(a);
      } else if (a.project_scope === "all") {
        shared.push(a);
      }
    }
    return { projectSpecific, shared, email };
  }, [automations, project.id]);

  const handleToggle = useCallback(async (id) => {
    setToggling(id);
    try {
      const updated = await toggleAutomation(id);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, enabled: updated.enabled } : a));
      setSelected(prev => (prev && prev.id === id) ? { ...prev, enabled: updated.enabled } : prev);
      toast.success(updated.enabled ? "Automation enabled." : "Automation disabled.");
    } catch {
      toast.error("Could not toggle automation.");
    } finally {
      setToggling(null);
    }
  }, []);

  const handleDelete = useCallback(async (id) => {
    setDeleting(id);
    try {
      await deleteAutomation(id);
      setAutomations(prev => prev.filter(a => a.id !== id));
      setSelected(prev => (prev && prev.id === id) ? null : prev);
      toast.success("Automation deleted.");
    } catch {
      toast.error("Could not delete automation.");
    } finally {
      setDeleting(null);
    }
  }, []);

  const handleSave = useCallback(async (id, payload) => {
    setSaving(true);
    try {
      const updated = await updateAutomation(id, payload);
      setAutomations(prev => prev.map(a => a.id === id ? { ...a, ...updated } : a));
      setSelected(prev => (prev && prev.id === id) ? { ...prev, ...updated } : prev);
      toast.success("Automation updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update automation.");
      throw e;
    } finally {
      setSaving(false);
    }
  }, []);

  const totalForProject = projectSpecific.length + shared.length + email.length;

  // Always render the section wrapper so data-testid is always in the DOM.
  return (
    <section data-testid="project-automations-panel">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightning size={14} className="text-primary" weight="fill" />
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Automations
            {!loading && (
              <span className="ml-2 text-slate-400 font-mono">{totalForProject}</span>
            )}
          </h3>
        </div>
        <button
          onClick={() => navigate("/automations")}
          className="text-[11px] text-primary hover:underline flex items-center gap-1"
          data-testid="manage-automations-link"
        >
          Manage all <ArrowRight size={10} />
        </button>
      </header>

      {loading ? (
        <div
          data-testid="project-automations-loading"
          className="flex items-center gap-2 text-slate-400 py-4"
        >
          <ArrowsClockwise size={14} className="animate-spin" />
          <span className="text-xs">Loading automations…</span>
        </div>
      ) : totalForProject === 0 ? (
        <div
          className="bg-slate-50 border border-dashed border-slate-200 rounded py-5 text-center"
          data-testid="project-automations-empty"
        >
          <p className="text-xs text-slate-400">No automations linked to this project.</p>
          <button
            onClick={() => navigate("/automations")}
            className="text-[11px] font-semibold text-primary hover:underline mt-1.5"
          >
            Create one
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <AutomationsSection
            testId="project-section-specific"
            title="Project-Specific"
            description="Scoped to this project"
            icon={Target}
            accent="text-emerald-500"
            automations={projectSpecific}
            onOpen={setSelected}
            onToggle={handleToggle}
            onDelete={handleDelete}
            toggling={toggling}
            deleting={deleting}
            emptyHint="None scoped to this project."
          />
          <AutomationsSection
            testId="project-section-shared"
            title="Shared (All Projects)"
            description="Applies across every project"
            icon={Globe}
            accent="text-blue-500"
            automations={shared}
            onOpen={setSelected}
            onToggle={handleToggle}
            onDelete={handleDelete}
            toggling={toggling}
            deleting={deleting}
            emptyHint="No automations apply to all projects."
          />
          <AutomationsSection
            testId="project-section-email"
            title="Email Automations"
            description="Triggered by new Inbox emails"
            icon={Envelope}
            accent="text-violet-500"
            automations={email}
            onOpen={setSelected}
            onToggle={handleToggle}
            onDelete={handleDelete}
            toggling={toggling}
            deleting={deleting}
            emptyHint="No email-triggered automations yet."
          />
        </div>
      )}

      {selected && (
        <AutomationDetailModal
          automation={selected}
          projects={projects}
          onSave={handleSave}
          onDelete={handleDelete}
          onToggle={handleToggle}
          onClose={() => setSelected(null)}
          busy={
            saving
              ? "saving"
              : deleting === selected.id
              ? "deleting"
              : toggling === selected.id
              ? "toggling"
              : null
          }
        />
      )}
    </section>
  );
}
